import React, { useState } from 'react';
import PfpSelect from './PfpSelect';
import TagInput from '../TagInput';
import { api } from '../api';

import { auth } from './firebase';
import { GoogleAuthProvider, signInWithPopup, getAdditionalUserInfo } from 'firebase/auth';
import '../TagInput.css';

const COUNTRIES = [
  "Argentina", "Australia", "Austria", "Belgium", "Brazil", "Canada", "Chile", "China", "Colombia", 
  "Czech Republic", "Denmark", "Egypt", "Finland", "France", "Germany", "Greece", "Hungary", "India", 
  "Indonesia", "Ireland", "Israel", "Italy", "Japan", "Malaysia", "Mexico", "Netherlands", "New Zealand", 
  "Norway", "Pakistan", "Peru", "Philippines", "Poland", "Portugal", "Romania", "Russia", "Saudi Arabia", 
  "Singapore", "South Africa", "South Korea", "Spain", "Sweden", "Switzerland", "Thailand", "Turkey", 
  "Ukraine", "United Arab Emirates", "United Kingdom", "United States", "Vietnam", "Other"
];

function SignupForm({ onComplete, loading = false, isUpgrade = false }) {
  const generateRandomUsername = () => {
    const adjectives = ['Happy', 'Lucky', 'Sunny', 'Clever', 'Brave', 'Calm', 'Witty', 'Fuzzy', 'Jolly', 'Kind', 'Swift', 'Silent', 'Cosmic', 'Neon', 'Hyper', 'Retro', 'Glitch', 'Cyber'];
    const nouns = ['Panda', 'Tiger', 'Lion', 'Bear', 'Fox', 'Wolf', 'Eagle', 'Hawk', 'Owl', 'Seal', 'Ninja', 'Star', 'Moon', 'Sun', 'Ghost', 'Bot', 'Byte', 'Pixel'];
    const adj = adjectives[Math.floor(Math.random() * adjectives.length)];
    const noun = nouns[Math.floor(Math.random() * nouns.length)];
    const num = Math.floor(Math.random() * 1000);
    return `${adj}${noun}${num}`;
  };

  const [formData, setFormData] = useState({
    username: generateRandomUsername(),
    email: '',
    name: '', // New field
    age: '',
    gender: 'prefer-not-to-say', // Default
    country: '',
    pfp: '',
    pfp_background: '',
    interests: [],
  });
  
  // Local state for DOB calculation
  const [dob, setDob] = useState('');
  
  const [countrySearch, setCountrySearch] = useState('');
  const [showCountryDropdown, setShowCountryDropdown] = useState(false);
  const [showPfpSelect, setShowPfpSelect] = useState(false);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 100 }, (_, i) => currentYear - i);
  const months = [
    { value: '01', label: 'January' },
    { value: '02', label: 'February' },
    { value: '03', label: 'March' },
    { value: '04', label: 'April' },
    { value: '05', label: 'May' },
    { value: '06', label: 'June' },
    { value: '07', label: 'July' },
    { value: '08', label: 'August' },
    { value: '09', label: 'September' },
    { value: '10', label: 'October' },
    { value: '11', label: 'November' },
    { value: '12', label: 'December' },
  ];

  // Helper to calculate age from DOB
  const handleDatePartChange = (part, value) => {
    let [y, m] = (dob || '-').split('-');
    if (part === 'year') y = value;
    if (part === 'month') m = value;

    const newDob = `${y || ''}-${m || ''}`;
    setDob(newDob);

    if (y && m) {
      const yearNum = parseInt(y);
      const monthNum = parseInt(m);
      const today = new Date();
      let age = today.getFullYear() - yearNum;
      const monthDiff = (today.getMonth() + 1) - monthNum;
      if (monthDiff < 0) {
        age--;
      }
      setFormData(prev => ({ ...prev, age: age }));
    } else {
      setFormData(prev => ({ ...prev, age: '' }));
    }
  };

  const handlePfpSave = ({ pfp, bg }) => {
    setFormData(prev => ({ ...prev, pfp, pfp_background: bg }));
    setShowPfpSelect(false);
  };

  const handleRegenerateUsername = (e) => {
    e.preventDefault();
    setFormData(prev => ({ ...prev, username: generateRandomUsername() }));
  };

  // --- GOOGLE AUTH HANDLER (For new users) ---
  const handleGoogleAuth = async () => {
    if (loading || isSubmitting) return;
    try {
        setIsSubmitting(true);
        setError(''); 
        
        const provider = new GoogleAuthProvider();
        const result = await signInWithPopup(auth, provider);
        const user = result.user;
        // Contains user.email
        const details = getAdditionalUserInfo(result);
    
        // This will now throw if the backend fails
        // [CHANGE 8] Pass user.email as the 3rd argument!
        const { userId } = await api.generateUserId(user.uid, formData.username, user.email);
        
        onComplete({ 
            uid: user.uid, 
            email: user.email,
            username: formData.username,
            isLogin: details.isNewUser ? false : true,
            userId,
        });

    } catch (err) {
        console.error("Signup Error:", err);
        setError(err.message || 'Google authentication failed.'); 
    } finally {
        setIsSubmitting(false);
    }
  };

  // --- UPGRADE SUBMIT HANDLER ---
  const handleUpgradeSubmit = (e) => {
    e.preventDefault();
    setError('');

    if (!formData.name || !formData.name.trim()) {
      setError('Please enter your display name.');
      return;
    }
    if (formData.age === '') {
      setError('Please enter your birth date.');
      return;
    }
    if (parseInt(formData.age) < 15) {
      setError('Your age is not eligible.');
      return;
    }
    if (!formData.gender) {
      setError('Please select your gender.');
      return;
    }
    if (!formData.country) {
      setError('Please select your country.');
      return;
    }

    // ✅ FIX: Add 'profile_completed: true' so the backend knows to unlock 50 matches
    onComplete({ 
        ...formData, 
        profile_completed: true 
    });
  };

  const getInitials = (name) => {
    return name ? name.charAt(0).toUpperCase() : '?';
  };

  const filteredCountries = COUNTRIES.filter(c => 
    c.toLowerCase().includes(countrySearch.toLowerCase())
  );

  // ==========================================
  // VIEW: PROFILE CUSTOMIZATION POPUP (Upgrade)
  // ==========================================
  if (isUpgrade) {
    return (
      <div className="fixed inset-0 z-[60] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 animate-in fade-in duration-200 font-sans">
        <div className="bg-[#0a0a0a] border border-white/10 rounded-[32px] overflow-hidden shadow-2xl flex flex-col max-h-[90vh] w-full max-w-md">
          
          {/* Header */}
          <div className="relative bg-gradient-to-r from-[#ffbd59] to-[#ff907c] p-6 text-center shrink-0">
            <div className="absolute top-0 left-0 w-full h-full bg-black/10" />
            <div className="relative z-10">
              <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-2 backdrop-blur-sm shadow-lg">
                 <span className="text-2xl">🔥</span>
              </div>
              <h2 className="text-2xl font-black text-black uppercase tracking-tight leading-none">Unlock 50 Matches</h2>
              <p className="text-black/70 text-xs font-bold mt-1">Complete your profile to start chatting</p>
            </div>
          </div>

          {/* Scrollable Content */}
          <div className="flex-1 overflow-y-auto p-6 space-y-6 [scrollbar-width:thin] [scrollbar-color:rgba(255,255,255,0.1)_transparent] [&::-webkit-scrollbar]:w-[3px] [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-white/10 [&::-webkit-scrollbar-thumb]:rounded-full">
            
            {/* 1. Avatar Section (Top) */}
            <div className="flex flex-col items-center">
              <div className="relative group cursor-pointer" onClick={() => setShowPfpSelect(true)}>
                <div className="w-28 h-28 rounded-full overflow-hidden shadow-2xl ring-4 ring-white/5 group-hover:ring-[#ffbd59]/50 transition-all relative">
                   {/* Background */}
                   <div 
                     className={`absolute inset-0 ${formData.pfp_background ? 'bg-black' : 'bg-gradient-to-br from-[#ffbd59] to-[#ff907c]'}`} 
                     style={formData.pfp_background ? { backgroundImage: `url(${formData.pfp_background})`, backgroundSize: 'cover', backgroundPosition: 'center' } : {}} 
                   />
                   {/* Avatar Image */}
                   <div className="absolute inset-0 flex items-center justify-center">
                     {formData.pfp ? (
                       <img src={formData.pfp} alt="Profile" className="w-full h-full object-contain" />
                     ) : (
                       <span className="text-3xl font-bold text-white/90">{getInitials(formData.username)}</span>
                     )}
                   </div>
                   {/* Edit Overlay */}
                   <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                     <svg className="w-8 h-8 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 20h9"></path><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"></path></svg>
                   </div>
                </div>
              </div>
            </div>

            {/* Form Fields */}
            <div className="space-y-5">
              
              {/* Name */}
              <div>
                <label className="block text-xs font-bold text-[#fefefe]/40 uppercase tracking-wider mb-2 ml-1">Display Name</label>
                <input 
                  type="text" 
                  value={formData.name} 
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  placeholder="What should we call you?" 
                  className="w-full bg-white/5 border border-white/10 rounded-2xl px-5 py-4 text-white placeholder:text-white/20 focus:outline-none focus:ring-2 focus:ring-[#ffbd59] transition-all"
                />
              </div>

              {/* Username (with Refresh) */}
              <div>
                <label className="block text-xs font-bold text-[#fefefe]/40 uppercase tracking-wider mb-2 ml-1">Username (Anonymous)</label>
                <div className="flex gap-2">
                  <input 
                    type="text" 
                    value={formData.username} 
                    readOnly 
                    className="w-full bg-white/5 border border-white/10 rounded-2xl px-5 py-4 text-white/70 cursor-default focus:outline-none" 
                  />
                  <button 
                    onClick={handleRegenerateUsername}
                    className="px-5 bg-white/5 hover:bg-white/10 border border-white/10 rounded-2xl text-white transition-all"
                    title="Generate new username"
                  >
                    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21.5 2v6h-6M2.5 22v-6h6M2 11.5a10 10 0 0 1 18.8-4.3M22 12.5a10 10 0 0 1-18.8 4.3"/></svg>
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                {/* Birth Date */}
                <div>
                  <label className="block text-xs font-bold text-[#fefefe]/40 uppercase tracking-wider mb-2 ml-1">
                    Birth Date
                  </label>
                  <div className="flex gap-2">
                    <div className="relative w-[60%]">
                      <select 
                        value={(dob || '-').split('-')[1] || ''}
                        onChange={(e) => handleDatePartChange('month', e.target.value)}
                        className="w-full bg-white/5 border border-white/10 rounded-2xl px-3 py-3 pr-8 text-white text-sm focus:outline-none focus:ring-2 focus:ring-[#ffbd59] appearance-none cursor-pointer"
                      >
                        <option value="" className="text-black">Month</option>
                        {months.map(m => <option key={m.value} value={m.value} className="text-black">{m.label}</option>)}
                      </select>
                      <div className="absolute inset-y-0 right-3 flex items-center pointer-events-none text-white/50">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" /></svg>
                      </div>
                    </div>
                    <div className="relative w-[40%]">
                      <select 
                        value={(dob || '-').split('-')[0] || ''}
                        onChange={(e) => handleDatePartChange('year', e.target.value)}
                        className="w-full bg-white/5 border border-white/10 rounded-2xl px-3 py-3 pr-8 text-white text-sm focus:outline-none focus:ring-2 focus:ring-[#ffbd59] appearance-none cursor-pointer"
                      >
                        <option value="" className="text-black">Year</option>
                        {years.map(y => <option key={y} value={y} className="text-black">{y}</option>)}
                      </select>
                      <div className="absolute inset-y-0 right-3 flex items-center pointer-events-none text-white/50">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" /></svg>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Gender */}
                <div>
                  <label className="block text-xs font-bold text-[#fefefe]/40 uppercase tracking-wider mb-2 ml-1">
                    Gender
                  </label>
                  <div className="relative">
                    <select 
                      value={formData.gender} 
                      onChange={(e) => setFormData({...formData, gender: e.target.value})}
                      className="w-full bg-white/5 border border-white/10 rounded-2xl px-4 py-4 pr-10 text-white focus:outline-none focus:ring-2 focus:ring-[#ffbd59] appearance-none cursor-pointer"
                    >
                      <option value="prefer-not-to-say" className="text-black">Prefer not to say</option>
                      <option value="male" className="text-black">Male</option>
                      <option value="female" className="text-black">Female</option>
                      <option value="non-binary" className="text-black">Non-binary</option>
                    </select>
                    <div className="absolute inset-y-0 right-4 flex items-center pointer-events-none text-white/50">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" /></svg>
                    </div>
                  </div>
                </div>

                {/* Country */}
                <div className="col-span-2">
                  <label className="block text-xs font-bold text-[#fefefe]/40 uppercase tracking-wider mb-2 ml-1">
                    Country
                  </label>
                  <div className="relative z-20">
                    <div 
                        className="w-full bg-white/5 border border-white/10 rounded-2xl px-4 py-4 text-white flex items-center justify-between cursor-pointer focus-within:ring-2 focus-within:ring-[#ffbd59]"
                        onClick={() => setShowCountryDropdown(!showCountryDropdown)}
                    >
                        <span className={formData.country ? "text-white" : "text-white/50"}>
                            {formData.country || "Select Country"}
                        </span>
                        <svg className="w-4 h-4 text-white/50" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" /></svg>
                    </div>
                    
                    {showCountryDropdown && (
                        <div className="absolute bottom-full mb-2 left-0 w-full bg-[#1a1a1a] border border-white/10 rounded-2xl shadow-xl z-50 overflow-hidden max-h-60 flex flex-col">
                            <div className="p-2 border-b border-white/5 sticky top-0 bg-[#1a1a1a]">
                                <input
                                    type="text"
                                    value={countrySearch}
                                    onChange={(e) => setCountrySearch(e.target.value)}
                                    placeholder="Search country..."
                                    className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2 text-sm text-white focus:outline-none focus:ring-1 focus:ring-[#ffbd59]"
                                    autoFocus
                                />
                            </div>
                            <div className="overflow-y-auto flex-1">
                                {filteredCountries.map(c => (
                                    <div 
                                        key={c}
                                        onClick={() => { setFormData({...formData, country: c}); setCountrySearch(''); setShowCountryDropdown(false); }}
                                        className="px-4 py-3 hover:bg-white/5 text-white text-sm cursor-pointer transition-colors"
                                    >
                                        {c}
                                    </div>
                                ))}
                                {filteredCountries.length === 0 && (
                                    <div className="px-4 py-3 text-white/40 text-sm text-center">No countries found</div>
                                )}
                            </div>
                        </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Interests (Bottom) */}
              <div>
                <label className="block text-xs font-bold text-[#fefefe]/40 uppercase tracking-wider mb-2 ml-1">Interests</label>
                <TagInput tags={formData.interests} onTagsChange={(tags) => setFormData({...formData, interests: tags})} />
              </div>

            </div>
            
            {error && <p className="text-red-500 text-sm text-center">{error}</p>}
          </div>

          {/* Footer Buttons */}
          <div className="px-6 py-1.5 border-t border-white/5 bg-[#111] flex flex-col items-center gap-1">
            <button 
              onClick={handleUpgradeSubmit}
              disabled={loading}
              className="w-3/4 py-1.5 rounded-2xl bg-[#ffbd59] text-black font-bold hover:bg-[#ffbd59]/90 disabled:opacity-50 transition-all text-base shadow-lg shadow-[#ffbd59]/20"
            >
              Save & Continue
            </button>
            <button 
              onClick={() => onComplete(null)} // Pass null to close without saving
              className="w-full py-1 rounded-2xl bg-transparent text-white/40 font-medium hover:text-white transition-all text-sm"
            >
              Remind me later
            </button>
          </div>

          {/* PFP Select Modal Layer */}
          {showPfpSelect && (
            <div className="absolute inset-0 z-50">
               <PfpSelect
                onClose={() => setShowPfpSelect(false)}
                onSave={handlePfpSave}
                currentPfp={formData.pfp}
                currentBg={formData.pfp_background}
              />
            </div>
          )}
        </div>
      </div>
    );
  }

  // ==========================================
  // VIEW: DEFAULT GOOGLE SIGNUP (Welcome)
  // ==========================================
  return (
    <div className="min-h-screen bg-black text-white flex flex-col items-center justify-center p-4 font-sans selection:bg-[#ffbd59]/30">
      <div className="w-full max-w-md text-center">
        <img src="https://pub-43e3d36a956c411fb92f0c0771910642.r2.dev/logo-yellow.svg" alt="blahbluh" className="w-20 h-20 object-contain rounded-[22%] mx-auto mb-6 shadow-lg" />
        <h1 className="text-4xl font-bold text-[#fefefe] tracking-tight mb-2">blahbluh</h1>
        <p className="text-[#fefefe]/60 mb-10">Anonymous chat, real connections.</p>

        <div className="space-y-3 animate-in fade-in slide-in-from-bottom-4 duration-500">
          <button onClick={handleGoogleAuth} disabled={loading || isSubmitting} className="w-full py-3.5 rounded-xl bg-[#fefefe] text-black font-bold hover:bg-[#fefefe]/90 transition-all flex items-center justify-center gap-2 disabled:opacity-50">
            <svg className="w-5 h-5" viewBox="0 0 24 24"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.84z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>
            Continue with Google
          </button>
        </div>
        {error && <p className="text-red-500 text-sm mt-4">{error}</p>}
      </div>
    </div>
  );
}

export default SignupForm;